import React, { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

/**
 * Strategy performance visualization component
 * Provides multiple chart types and interactive features
 */
const PerformanceVisualization = ({ 
  strategyResults = [], 
  comparisonMode = false, 
  chartType = 'cumulative' 
}) => {
  const [selectedChartType, setSelectedChartType] = useState(chartType);
  const [selectedTimeframe, setSelectedTimeframe] = useState('all');
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);

  const chartData = useMemo(() => {
    if (!strategyResults.length) return [];
    
    // Transform strategy results into chart-ready data
    return strategyResults.map(result => ({
      strategy: result.strategy.name,
      performance: result.performance,
      raceHistory: result.raceHistory || []
    }));
  }, [strategyResults]);

  const performanceMetrics = useMemo(() => {
    if (!strategyResults.length) return null;
    
    const firstResult = strategyResults[0];
    return {
      totalProfit: firstResult.performance?.netProfit || 0,
      roi: firstResult.performance?.roi || 0,
      winRate: firstResult.performance?.winRate || 0,
      maxDrawdown: firstResult.performance?.maxDrawdown || 0,
      totalBets: firstResult.performance?.totalBets || 0,
      strikeRate: firstResult.performance?.strikeRate || 0
    };
  }, [strategyResults]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  if (!strategyResults.length) {
    return (
      <Card className="p-8 text-center">
        <div className="text-gray-500">
          <div className="text-lg font-medium mb-2">No strategy data available</div>
          <div className="text-sm">Configure and apply a strategy to see performance visualization</div>
        </div>
      </Card>
    );
  }

  return (
    <div className="performance-visualization space-y-4">
      {/* Chart Controls */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-2">
          <Select value={selectedChartType} onValueChange={setSelectedChartType}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Chart Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cumulative">Cumulative P&L</SelectItem>
              <SelectItem value="roi">ROI Over Time</SelectItem>
              <SelectItem value="drawdown">Drawdown Analysis</SelectItem>
              <SelectItem value="distribution">Profit Distribution</SelectItem>
            </SelectContent>
          </Select>

          <Select value={selectedTimeframe} onValueChange={setSelectedTimeframe}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Timeframe" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="1y">Last Year</SelectItem>
              <SelectItem value="6m">Last 6 Months</SelectItem>
              <SelectItem value="3m">Last 3 Months</SelectItem>
              <SelectItem value="1m">Last Month</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2">
          <Button size="sm" variant="outline">
            Export Chart
          </Button>
          <Button size="sm" variant="outline">
            Full Screen
          </Button>
        </div>
      </div>

      {/* Performance Metrics Summary */}
      {performanceMetrics && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="p-3">
            <div className="text-xs text-gray-500">Total Profit</div>
            <div className={`text-lg font-semibold ${
              performanceMetrics.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {formatCurrency(performanceMetrics.totalProfit)}
            </div>
          </Card>
          
          <Card className="p-3">
            <div className="text-xs text-gray-500">ROI</div>
            <div className={`text-lg font-semibold ${
              performanceMetrics.roi >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {formatPercentage(performanceMetrics.roi)}
            </div>
          </Card>
          
          <Card className="p-3">
            <div className="text-xs text-gray-500">Win Rate</div>
            <div className="text-lg font-semibold">
              {formatPercentage(performanceMetrics.winRate)}
            </div>
          </Card>
          
          <Card className="p-3">
            <div className="text-xs text-gray-500">Max Drawdown</div>
            <div className="text-lg font-semibold text-red-600">
              {formatCurrency(Math.abs(performanceMetrics.maxDrawdown))}
            </div>
          </Card>
          
          <Card className="p-3">
            <div className="text-xs text-gray-500">Total Bets</div>
            <div className="text-lg font-semibold">
              {performanceMetrics.totalBets.toLocaleString()}
            </div>
          </Card>
          
          <Card className="p-3">
            <div className="text-xs text-gray-500">Strike Rate</div>
            <div className="text-lg font-semibold">
              {formatPercentage(performanceMetrics.strikeRate)}
            </div>
          </Card>
        </div>
      )}

      {/* Chart Area */}
      <Card className="p-4">
        <Tabs value={selectedChartType} onValueChange={setSelectedChartType}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="cumulative">Cumulative</TabsTrigger>
            <TabsTrigger value="roi">ROI</TabsTrigger>
            <TabsTrigger value="drawdown">Drawdown</TabsTrigger>
            <TabsTrigger value="distribution">Distribution</TabsTrigger>
          </TabsList>
          
          <TabsContent value="cumulative" className="mt-4">
            <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 rounded">
              <div className="text-center text-gray-500">
                <div className="text-lg font-medium">Cumulative P&L Chart</div>
                <div className="text-sm">Chart implementation will be added here</div>
                <div className="text-xs mt-2">Using Recharts or similar charting library</div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="roi" className="mt-4">
            <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 rounded">
              <div className="text-center text-gray-500">
                <div className="text-lg font-medium">ROI Over Time Chart</div>
                <div className="text-sm">Chart implementation will be added here</div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="drawdown" className="mt-4">
            <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 rounded">
              <div className="text-center text-gray-500">
                <div className="text-lg font-medium">Drawdown Analysis Chart</div>
                <div className="text-sm">Chart implementation will be added here</div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="distribution" className="mt-4">
            <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 rounded">
              <div className="text-center text-gray-500">
                <div className="text-lg font-medium">Profit Distribution Chart</div>
                <div className="text-sm">Chart implementation will be added here</div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </Card>

      {/* Comparison Mode */}
      {comparisonMode && strategyResults.length > 1 && (
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Strategy Comparison</h3>
          <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 rounded">
            <div className="text-center text-gray-500">
              <div className="text-lg font-medium">Comparative Analysis Chart</div>
              <div className="text-sm">Side-by-side strategy comparison will be implemented here</div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default PerformanceVisualization;